import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { SortOption, TaskStage } from '../lib/types';

export interface TaskData {
  id: string;
  title: string;
  description: string | null;
  stage: TaskStage;
  overlay: 'paused' | 'on-hold' | 'cancelled' | null;
  conversation_id: string;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskFilters {
  stages: TaskStage[];
  search: string;
  sortBy: SortOption;
}

export function useTasks({ enabled = false, pollInterval = 15000 }: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFiltersState] = useState<TaskFilters>({ stages: [], search: '', sortBy: 'newest' });
  const filtersRef = useRef(filters);

  const hasLoadedOnce = useRef(false);
  const fetchInFlightRef = useRef(false);

  const doFetch = useCallback(() => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    const f = filtersRef.current;
    const query: Record<string, string> = {};
    if (f.stages.length === 1) query.stage = f.stages[0].replace(/-/g, '_');
    if (f.search) query.search = f.search;
    apiClient
      .listTasks(Object.keys(query).length > 0 ? query : undefined)
      .then((res) => {
        setTasks(res.data.map((t) => ({ ...t, stage: t.stage.replace(/_/g, '-'), overlay: t.overlay_state ? t.overlay_state.replace(/_/g, '-') : null })));
        setHasMore(res.has_more);
        hasLoadedOnce.current = true;
        setStatus('success');
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!hasLoadedOnce.current) {
          setStatus('error');
        }
        setIsRefreshing(false);
      })
      .finally(() => { fetchInFlightRef.current = false; });
  }, [apiClient]);

  // Ref to doFetch so effects don't depend on its identity
  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  // Initial fetch — runs once on mount
  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    doFetchRef.current();
  }, [enabled]);

  // Polling — single stable interval
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      if (fetchInFlightRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setIsRefreshing(true);
      doFetchRef.current();
    }, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval]);

  // Filter/reload trigger: wrap setFilters and reload to call doFetch directly (no useEffect)
  const setFilters = useCallback((f: TaskFilters) => {
    filtersRef.current = f;
    setFiltersState(f);
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      // Clear in-flight guard to allow the filter fetch to proceed
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  const reload = useCallback(() => {
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectTask = useCallback((id: string | null) => setSelectedId(id), []);

  const counts = useMemo(() => {
    const c: Record<TaskStage, number> = { 'not-started': 0, 'awaiting-input': 0, working: 0, complete: 0 };
    for (const t of tasks) { c[t.stage] = (c[t.stage] ?? 0) + 1; }
    return c;
  }, [tasks]);

  const updateTaskOverlay = useCallback(async (id: string, overlayState: string | null) => {
    let prevOverlay: string | null | undefined;
    setTasks((ts) => {
      prevOverlay = (ts as Array<{ id: string; overlay: string | null }>).find((t) => t.id === id)?.overlay;
      return (ts as Array<{ id: string }>).map((t) => t.id === id ? { ...t, overlay: overlayState ? overlayState.replace(/_/g, '-') : null } : t);
    });
    try {
      await apiClient.updateTask(id, { overlay_state: overlayState });
    } catch {
      setTasks((ts) => (ts as Array<{ id: string }>).map((t) => t.id === id ? { ...t, overlay: prevOverlay ?? null } : t));
    }
  }, [apiClient]);

  const loadMore = useCallback(async () => {
    const last = tasks[tasks.length - 1] as { id: string } | undefined;
    if (!last) return;
    const query: Record<string, string> = { starting_after: last.id };
    const f = filtersRef.current;
    if (f.stages.length === 1) query.stage = f.stages[0].replace(/-/g, '_');
    try {
      const res = await apiClient.listTasks(query);
      setTasks((prev) => [...prev, ...res.data.map((t) => ({ ...t, stage: t.stage.replace(/_/g, '-'), overlay: t.overlay_state ? t.overlay_state.replace(/_/g, '-') : null }))]);
      setHasMore(res.has_more);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
      setStatus('error');
    }
  }, [apiClient, tasks]);

  const mergeTaskUpdate = useCallback((event: { type: string; task_id: string; conversation_id?: string; title?: string; description?: string; stage?: string; timestamp?: string }) => {
    if (event.type === 'task_created') {
      setTasks((prev) => {
        if ((prev as Array<{ id: string }>).some((t) => t.id === event.task_id)) return prev;
        return [{ id: event.task_id, title: event.title ?? '', description: event.description ?? null, stage: 'not-started', overlay: null, conversation_id: event.conversation_id ?? '', assigned_to: null, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, ...prev];
      });
    } else if (event.type === 'task_status_update' && event.stage) {
      setTasks((prev) => (prev as Array<{ id: string }>).map((t) => t.id === event.task_id ? { ...t, stage: event.stage!.replace(/_/g, '-') } : t));
    }
  }, []);

  const createTask = useCallback(async (params: { title: string; description?: string }) => {
    const conv = await apiClient.createConversation({ title: `[Task] ${params.title}` });
    const task = await apiClient.createTask({
      title: params.title,
      conversation_id: conv.id,
      description: params.description,
    });
    setTasks((prev) => [
      {
        id: task.id,
        title: task.title,
        description: task.description ?? null,
        stage: (task.stage.replace(/_/g, '-') as TaskStage),
        overlay: task.overlay_state ? (task.overlay_state.replace(/_/g, '-') as TaskData['overlay']) : null,
        conversation_id: task.conversation_id,
        assigned_to: task.assigned_to,
        created_at: task.created_at,
        updated_at: task.updated_at,
      },
      ...prev,
    ]);
    return task;
  }, [apiClient]);

  const transitionTask = useCallback(async (id: string, newStage: TaskStage) => {
    let prevStage: TaskStage | undefined;
    setTasks((ts) => {
      prevStage = (ts as TaskData[]).find((t) => t.id === id)?.stage;
      return (ts as TaskData[]).map((t) => t.id === id ? { ...t, stage: newStage } : t);
    });
    try {
      await apiClient.transitionTask(id, { stage: newStage.replace(/-/g, '_') });
    } catch {
      if (prevStage) setTasks((ts) => (ts as TaskData[]).map((t) => t.id === id ? { ...t, stage: prevStage! } : t));
    }
  }, [apiClient]);

  // TODO(Epic 14, AC11): Wire PushNotificationManager — fire notification when stage → awaiting_input or complete
  return { tasks, status, isRefreshing, error, counts, selectedId, selectTask, reload, filters, setFilters, hasMore, loadMore, updateTaskOverlay, transitionTask, mergeTaskUpdate, createTask };
}
